import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthStore } from '@/shared/state/authstore'
import {
  getNotificationsConnection,
  startNotificationsConnection,
} from '@/shared/realtime/notificationsConnection'
import {
  playIncidentChime,
  showIncidentDesktopNotification,
} from '@/shared/lib/incidentAlertCues'
import { guardLocationKeys } from '@/entities/guard-location'
import { incidentKeys } from './queries'
import { useNewIncidentsStore, type IncidentEventPriority } from './newIncidentsStore'

type ReceivedEvent = {
  id: number
  accountId: number
  event: string
  priority: string
  title: string
  body: string | null
  payload: string | null
  sourceId: number | null
  createdOn: string
}

const incidentEvents = new Set([
  'IncidentSubmitted',
  'IncidentStatusChanged',
  'IncidentFollowUpAdded',
  'SosTriggered',
  'ClientSosTriggered',
  'ClientSosLocationReceived',
  // Invalidation only, deliberately not in freshIncidentEvents: a stand-down must never chime
  // and toast like a new alarm. Clearing the pin is the signal.
  'SosCancelled',
])

const freshIncidentEvents = new Set(['IncidentSubmitted', 'SosTriggered', 'ClientSosTriggered'])

const sosEvents = new Set(['SosTriggered', 'ClientSosTriggered'])

function normalizePriority(priority: string): IncidentEventPriority {
  if (priority === 'Critical') return 'Critical'
  if (priority === 'Info') return 'Info'
  return 'Standard'
}

type IncidentPayload = {
  siteName?: string
  subtype?: string
  guardName?: string
  severity?: string
  incidentRef?: string
}

function parseIncidentPayload(payload: string | null): IncidentPayload {
  if (!payload) return {}
  try {
    const parsed = JSON.parse(payload) as Record<string, unknown>
    return {
      siteName: typeof parsed.siteName === 'string' ? parsed.siteName : undefined,
      subtype: typeof parsed.subtype === 'string' ? parsed.subtype : undefined,
      guardName: typeof parsed.guardName === 'string' ? parsed.guardName : undefined,
      severity: typeof parsed.severity === 'string' ? parsed.severity : undefined,
      incidentRef: typeof parsed.incidentRef === 'string' ? parsed.incidentRef : undefined,
    }
  } catch {
    return {}
  }
}

function showIncidentToast(n: ReceivedEvent, priority: IncidentEventPriority): void {
  const p = parseIncidentPayload(n.payload)
  const severity = p.severity ?? (priority === 'Critical' ? 'Critical' : 'New')
  const baseHeading = sosEvents.has(n.event)
    ? n.event === 'SosTriggered'
      ? 'SOS — a guard raised a panic alarm'
      : 'SOS — a client raised an emergency alert'
    : `New incident raised — ${severity}`
  const heading = p.incidentRef ? `${p.incidentRef} · ${baseHeading}` : baseHeading
  const detail =
    [p.subtype, p.siteName, p.guardName].filter(Boolean).join(' · ') || n.body || undefined
  const options = { description: detail, duration: sosEvents.has(n.event) ? Infinity : 10_000 }
  if (priority === 'Critical') toast.error(heading, options)
  else toast.warning(heading, options)
}

function handleFreshIncident(n: ReceivedEvent): void {
  if (n.sourceId == null) return
  const priority = normalizePriority(n.priority)
  const state = useNewIncidentsStore.getState()

  state.recordArrival({
    incidentId: n.sourceId,
    arrivedAt: Date.now(),
    priority,
  })

  showIncidentToast(n, priority)

  if (state.soundEnabled) playIncidentChime(priority)
  if (state.desktopEnabled) {
    showIncidentDesktopNotification({
      incidentId: n.sourceId,
      priority,
      title: n.title,
      body: n.body,
    })
  }
}

export function useIncidentRealtime(): void {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((s) => s.accessToken)
  const accountId = useAuthStore((s) => s.accountId)

  useEffect(() => {
    if (!accessToken || accountId == null) return

    const connection = getNotificationsConnection()
    const onReceived = (n: ReceivedEvent) => {
      if (n.accountId !== accountId) return
      if (!incidentEvents.has(n.event)) return
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all })
      // The live map reads its incidents from the guard-location feed now, so invalidating the
      // incident keys alone would leave that drawer stale.
      void queryClient.invalidateQueries({ queryKey: guardLocationKeys.live })
      if (freshIncidentEvents.has(n.event)) handleFreshIncident(n)
    }

    connection.on('notificationReceived', onReceived)

    void startNotificationsConnection()

    return () => {
      connection.off('notificationReceived', onReceived)
    }
  }, [accessToken, accountId, queryClient])
}
